import React from 'react';
import Zoom from '@mui/material/Zoom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import BoxContainer from 'src/components/UI/container/BoxContainer';

// Disc tint + default icon + announce semantics per status variant. Colours come
// from semantic tokens (App.css), not the mockup hex, so they stay AA in
// light/dark and consistent with the submit button / link-status pills.
const VARIANTS = {
  success: {
    bg: 'var(--color-active-bg)',
    fg: 'var(--color-active-fg)',
    border: 'rgba(42, 106, 32, 0.25)',
    Icon: CheckRoundedIcon,
    role: 'status',
    live: 'polite'
  },
  error: {
    bg: 'var(--color-error-bg)',
    fg: 'var(--color-error-fg)',
    border: 'rgba(179, 38, 30, 0.25)',
    Icon: LinkOffRoundedIcon,
    role: 'alert',
    live: 'assertive'
  }
};

// Shared centred status card for terminal patient-facing screens: the submission
// confirmation (success) and the invalid/expired-link page (error) are the same
// object — a compact icon disc, heading, sub-line, optional note, and optional
// extra content (children). Keeping one implementation locks the two visually.
// Focus moves to the heading and the region announces (role + aria-live) for
// screen-reader users; the icon is decorative (heading text carries the meaning).
export default function StatusCard({
  variant = 'success',
  icon,
  heading,
  subtext,
  note,
  children
}) {
  const v = VARIANTS[variant] || VARIANTS.success;
  const headingRef = React.useRef(null);

  React.useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const IconEl = v.Icon;

  return (
    <BoxContainer>
      <Stack
        spacing={2.5}
        role={v.role}
        aria-live={v.live}
        sx={{ alignItems: 'center', textAlign: 'center' }}
      >
        <Zoom in={true}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: v.bg,
              border: '1px solid',
              borderColor: v.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {icon ?? <IconEl aria-hidden sx={{ fontSize: 28, color: v.fg }} />}
          </Box>
        </Zoom>

        <Box>
          <Typography
            variant="h5"
            component="h1"
            ref={headingRef}
            tabIndex={-1}
            sx={{
              fontWeight: 700,
              color: 'var(--color-navy)',
              outline: 'none'
            }}
          >
            {heading}
          </Typography>
          {subtext && (
            <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
              {subtext}
            </Typography>
          )}
        </Box>

        {children}
        {note && <Box sx={{ width: '100%' }}>{note}</Box>}
      </Stack>
    </BoxContainer>
  );
}
